import type { ManifestWorkspaceView } from '../../types.js';
import { UMB_WORKSPACE_EDITOR_CONTEXT } from './workspace-editor.context-token.js';
import { UmbWorkspaceViewController } from './workspace-view.controller.js';
import { UmbBasicState, mergeObservables } from '@umbraco-cms/backoffice/observable-api';
import { UmbContextBase } from '@umbraco-cms/backoffice/class-api';
import { UmbExtensionsManifestInitializer } from '@umbraco-cms/backoffice/extension-api';
import { umbExtensionsRegistry } from '@umbraco-cms/backoffice/extension-registry';
import type { UmbControllerHost } from '@umbraco-cms/backoffice/controller-api';
import type { UmbDeepPartialObject } from '@umbraco-cms/backoffice/utils';
import type { UmbVariantId } from '@umbraco-cms/backoffice/variant';

export class UmbWorkspaceEditorContext extends UmbContextBase {
	//
	#init: Promise<void>;
	/**
	 * State holding the permitted Workspace Views as a Workspace View Context
	 */

	#manifests = new UmbBasicState<Array<ManifestWorkspaceView>>([]);
	#overrides = new UmbBasicState<Array<UmbDeepPartialObject<ManifestWorkspaceView>>>([]);
	public readonly views = mergeObservables(
		[this.#manifests.asObservable(), this.#overrides.asObservable()],
		([manifests, overrides]): Array<UmbWorkspaceViewController> => {
			let contexts = this.#contexts;

			// remove ones that are no longer contained in the workspaceViews (And thereby make the new array):
			const contextsToKeep = contexts.filter((view) => {
				const keep = manifests.some((manifest) => manifest.alias === view.manifest.alias);
				if (!keep) {
					view.destroy();
				}
				return keep;
			});

			const hasDiff = contextsToKeep.length !== manifests.length;
			if (hasDiff) {
				contexts = [...contextsToKeep];

				// Add ones that are new:
				manifests
					.filter((manifest) => !contextsToKeep.some((x) => x.manifest.alias === manifest.alias))
					.forEach((manifest) => {
						const context = new UmbWorkspaceViewController(this, manifest);
						context.setVariantId(this.#variantId);
						context.setTitle(manifest.meta.label);
						context.inherit();
						contexts.push(context);
					});
			}

			// Apply overrides:
			let hasOverrideChange = false;
			contexts.forEach((context) => {
				const override = overrides.find((x) => x.alias === context.manifest.alias);
				if (override) {
					// Test to see if there is a change, to avoid unnecessary updates, this prevents re-setting the manifest again and again. [NL]
					const overrideKeys = Object.keys(override) as Array<keyof ManifestWorkspaceView>;
					const hasOverrideDiff = overrideKeys.some((key) => context.manifest[key] !== override[key]);
					if (hasOverrideDiff) {
						context.manifest = {
							...context.manifest,
							...(override as ManifestWorkspaceView),
							meta: { ...context.manifest.meta, ...override.meta },
						};
						hasOverrideChange = true;
					}
				}
			});

			// sort contexts to match manifests weights:
			contexts.sort((a, b): number => (b.manifest.weight || 0) - (a.manifest.weight || 0));

			this.#contexts = contexts;
			// Return a new array reference when overrides changed, so distinctUntilChanged can detect it:
			return hasOverrideChange || hasDiff ? [...contexts] : contexts;
		},
		// Custom memoize method, to check context instance and manifest instance:
		(previousValue: Array<UmbWorkspaceViewController>, currentValue: Array<UmbWorkspaceViewController>): boolean => {
			return (
				previousValue === currentValue &&
				currentValue.every(
					(x) => x.manifest === previousValue.find((y) => y.manifest.alias === x.manifest.alias)?.manifest,
				)
			);
		},
	);
	// A storage and cache for the current contexts, to enable communicating to them and to avoid re-initializing them every time there is a change of manifests/overrides. [NL]
	#contexts = new Array<UmbWorkspaceViewController>();

	#variantId?: UmbVariantId;

	constructor(host: UmbControllerHost) {
		super(host, UMB_WORKSPACE_EDITOR_CONTEXT);

		this.#init = new UmbExtensionsManifestInitializer(
			this,
			umbExtensionsRegistry,
			'workspaceView',
			null,
			(workspaceViews) => {
				this.#manifests.setValue(workspaceViews.map((controller) => controller.manifest));
			},
		).asPromise();
	}

	setVariantId(variantId: UmbVariantId | undefined): void {
		this.#variantId = variantId;
		this.#contexts.forEach((view) => {
			view.setVariantId(variantId);
		});
	}

	setOverrides(overrides?: Array<UmbDeepPartialObject<ManifestWorkspaceView>>): void {
		this.#overrides.setValue(overrides ?? []);
	}

	async getViewContext(alias: string): Promise<UmbWorkspaceViewController | undefined> {
		await this.#init;
		return this.#contexts.find((view) => view.manifest.alias === alias);
	}
}
